import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import * as moment from 'moment';
import {IReferral} from '../domain/onBoarding/IReferral';
import {Account} from '../domain/accounts/account';
import {ETargetAccountStatus} from '../enums/ETargetAccountStatus';
import {ITargetAccount} from '../domain/accounts/ITargetAccount';
import {IInternationalTargetAccount} from '../domain/accounts/IInternationalTargetAccount';
import {IFrequentTransfer} from '../domain/accounts/IFrequentTransfer';

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  cardNumber: string;
  constructor(private http: HttpClient) {
  }

  getProfile() {
    return this.http.get('/accounts/profile');
  }

  getDetail() {
    return this.http.get('/accounts/detail');
  }

  cancelAccount() {
    return this.http.put('/accounts/cancel', null);
  }

  getMovements(accountId: number, startDate: Date = null, endDate: Date = null) {
    return this.http.get(`/accounts/statement?accountId=${accountId}${startDate != null ? `&startDate=${moment(startDate).format('YYYY-MM-DD')}` : ''}${startDate != null ? `&endDate=${moment(endDate).format('YYYY-MM-DD')}` : ''}`);
  }
  getMovementsOffset(accountId: number, startDate: string = null, endDate: string = null, zoneOffset: string = null) {
    return this.http.post('/accounts/movements', {
      accountId,
      startDate,
      endDate,
      zoneOffset
    });
  }

  getMyReferralsReport(startDate: Date = null, endDate: Date = null){
    return this.http.get(`/accounts/commissions/report?startDate=${moment(startDate).format('YYYY-MM-DD')}&endDate=${moment(endDate).format('YYYY-MM-DD')}`);
  }
  phoneNumberChange(password: string, phoneCountryCode: string, phoneNumber: string, reCaptchaToken: string) {
    return this.http.put('/onboarding/user/phone', {currentPassword: password, phoneCountryCode, phone: phoneNumber, reCaptchaToken});
  }

  emailChange(password: string, email: string) {
    return this.http.put('/onboarding/user/email', {currentPassword: password, email});
  }

  passwordChange(password: string, newPassword: string, newPasswordConfirm: string) {
    return this.http.put('/onboarding/user/passwd', {currentPassword: password, password: newPassword, matchingPassword: newPasswordConfirm});
  }

  activateCard() {
    return this.http.put('/accounts/activate', null);
  }

  otpVerification(otp: string, phoneCountryCode: string, phoneNumber: string) {
    return this.http.post('/onboarding/authorizations/otp/phone-validate', {
      otp,
      phoneCountryCode,
      cellphone: phoneNumber
    });
  }

  addReferrals(account: Account, referred: IReferral[]) {
    return this.http.post(`/accounts/${account.id}/referrals`, referred);
  }

  getTargetAccounts(status: ETargetAccountStatus[]) {
    return this.http.get(`/accounts/targetAccount?status=${status}`);
  }

  getInternationalTargetAccounts() {
    return this.http.get(`/accounts/target-beneficiaries`);
  }

  createTargetAccount(account: ITargetAccount) {
    return this.http.post(`/accounts/targetAccount`, account);
  }

  createInternationalTargetAccount(account: IInternationalTargetAccount) {
    return this.http.post(`/accounts/target-beneficiaries`, account);
  }

  editTargetAccount(account: ITargetAccount) {
    return this.http.put(`/accounts/targetAccount/${account.id}`, account);
  }

  editInternationalTargetAccount(account: IInternationalTargetAccount) {
    return this.http.put(`/accounts/target-beneficiaries/${account.id}`, account);
  }

  deleteTargetAccount(account: ITargetAccount) {
    return this.http.delete(`/accounts/targetAccount/${account.id}`);
  }

  deleteInternationalTargetAccount(account: IInternationalTargetAccount) {
    return this.http.delete(`/accounts/target-beneficiaries/${account.id}`);
  }

  getBalance(accountId: number) {
    return this.http.get(`/accounts/${accountId}/balance`);
  }

  getExchangeRate(amountRequested?: number) {
    const amount = amountRequested != null ? amountRequested : 10;
    return this.http.get(`/accounts/currencies/exchange-rate?sourceCurrencyCode=840&targetCurrencyCode=484&amount=${amount}&reference=Reference for inquiry`);
  }

  getTransferFees(transactionType: string) {
    return this.http.get(`/catalog/COMMISSIONS?type=${transactionType}`);
  }

  transfer(sourceAccount: Account, targetAccount: ITargetAccount, amount: number, reCaptchaToken: string, recurrentName?: string) {
    const json = {
      originAccountId: sourceAccount.id,
      destinationAccountId: targetAccount.id,
      targetType: targetAccount.type,
      amount,
      reCaptchaToken,
      frequentTransferName: recurrentName
    };
    return this.http.post(`/accounts/transfers`, json);
  }

  getFrequentTransfers() {
    return this.http.get(`/accounts/transfers/frequentTransfers`);
  }

  deleteFrequentTransfer(frequentTransfer: IFrequentTransfer) {
    return this.http.delete(`/accounts/transfers/frequentTransfers/${frequentTransfer.id}`);
  }

  getSensitiveData(mediaAccessId: number) {
    return this.http.get(`/accounts/media-access/${mediaAccessId}/sensitive`);
  }

  sendDisclosureEmail(email: string, fileName: string, file: string) {
    const formData = new FormData();
    formData.append(`attachments`, this.formatBase64ToFile(file, `${fileName}.png`));
    formData.append('body', JSON.stringify({email, mailjetTemplate: 'TICKET', variables: {}}));
    return this.http.post('/notifications/email/send/attachments', formData);
  }
  searchCard(cardNumber: string){
    return this.http.get(`/accounts/card/lookup?cardNumber=${cardNumber}`);
  }
  formatBase64ToFile(img, fileName): File {
    const arr = img.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, {type: mime});
  }

  transferConfirm(id: number, otp: string) {
    return this.http.post(`/accounts/transfers/confirm`, {
      confirmationId: id,
      confirmationCode: otp
    });
  }

  getStatementMonthly(date) {
    return this.http.get<any>(`/accounts/statementMonthly?statementDate=${date}`, {responseType: 'arraybuffer' as 'json'});
  }

  getAvailableStatement() {
    return this.http.get(`/accounts/statement/availableStatements`);
  }

  getStatementPDF(id: number) {
    return this.http.get<any>(`/accounts/statement/${id}`, {responseType: 'arraybuffer' as 'json'});
  }

  getReferrals() {
    return this.http.get('/accounts/referrals');
  }

  sendConfirmationEmail() {
    return this.http.get('/onboarding/authorizations/email/send-email-validation');
  }

  verifyPin(pin: string) {
    return this.http.post('/accounts/cards/verifyPIN', {currentPIN: pin});
  }

  changePin(oldPin: string, newPin: string) {
    return this.http.post('/accounts/cards/changePIN', {currentPIN: oldPin, newPIN: newPin});
  }

  resetPin(pin: string) {
    return this.http.post('/accounts/cards/resetPIN', {newPIN: pin});
  }
  getReferredBrokerLink(customerId: number){
    return this.http.get(`/accounts/customers/referred-broker-link/${customerId}`);
  }
  postReferredBroker(altairParentAgentId){
    return this.http.post(`/accounts/customers/associate-broker/${altairParentAgentId}`, null);
  }
  blockCard(cardStatus: boolean){
    return this.http.put(`/accounts/cards/suspend?suspend=${cardStatus}`, null);
  }
  sendContactUs(data) {
    return this.http.post('/accounts/contact', data);
  }

  getCardPrice() {
    return this.http.get('/accounts/cards/price');
  }
  cardPurchase() {
    return this.http.post('/accounts/cards/request', null);
  }
}
